import { useEffect } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { usePageMeta } from '@/hooks/usePageMeta';
import { UserPlus, CreditCard, FileText, Share2, MessageCircle, Users, Mail, Heart, Video, Globe, Shield, TrendingUp, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const HOW_IT_WORKS_DESCRIPTION =
  "See how Devotion Fund works in four steps: sign up, connect your bank, build your profile, and share your mission. Start receiving support in under 30 minutes.";

export default function HowItWorks() {
    usePageMeta('How It Works', HOW_IT_WORKS_DESCRIPTION);
    const { ref: workflowRef, isVisible: workflowVisible } = useScrollAnimation();
    const { ref: creatorRef, isVisible: creatorVisible } = useScrollAnimation();
    const { ref: donorRef, isVisible: donorVisible } = useScrollAnimation();
    const { ref: faqRef, isVisible: faqVisible } = useScrollAnimation();
    const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const faqs = [
        { question: t('howItWorksPage.faq.q1.question'), answer: t('howItWorksPage.faq.q1.answer') },
        { question: t('howItWorksPage.faq.q2.question'), answer: t('howItWorksPage.faq.q2.answer') },
        { question: t('howItWorksPage.faq.q3.question'), answer: t('howItWorksPage.faq.q3.answer') },
        { question: t('howItWorksPage.faq.q4.question'), answer: t('howItWorksPage.faq.q4.answer') },
        { question: t('howItWorksPage.faq.q5.question'), answer: t('howItWorksPage.faq.q5.answer') },
        { question: t('howItWorksPage.faq.q6.question'), answer: t('howItWorksPage.faq.q6.answer') },
    ];

    useEffect(() => {
        const jsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: { '@type': 'Answer', text: faq.answer },
            })),
        };
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-how-it-works-faq-jsonld', 'true');
        el.textContent = JSON.stringify(jsonLd);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, [faqs]);

    const steps = [
        {
            number: 1,
            icon: UserPlus,
            title: t('howItWorksPage.workflow.step1.title'),
            description: t('howItWorksPage.workflow.step1.description')
        },
        {
            number: 2,
            icon: CreditCard,
            title: t('howItWorksPage.workflow.step2.title'),
            description: t('howItWorksPage.workflow.step2.description')
        },
        {
            number: 3,
            icon: FileText,
            title: t('howItWorksPage.workflow.step3.title'),
            description: t('howItWorksPage.workflow.step3.description')
        },
        {
            number: 4,
            icon: Share2,
            title: t('howItWorksPage.workflow.step4.title'),
            description: t('howItWorksPage.workflow.step4.description')
        }
    ];

    const creatorFeatures = [
        {
            icon: FileText,
            title: t('howItWorksPage.creatorFeatures.missionProfile.title'),
            description: t('howItWorksPage.creatorFeatures.missionProfile.description'),
            benefits: [
                t('howItWorksPage.creatorFeatures.missionProfile.benefit1'),
                t('howItWorksPage.creatorFeatures.missionProfile.benefit2'),
                t('howItWorksPage.creatorFeatures.missionProfile.benefit3'),
                t('howItWorksPage.creatorFeatures.missionProfile.benefit4')
            ]
        },
        {
            icon: Mail,
            title: t('howItWorksPage.creatorFeatures.emailUpdates.title'),
            description: t('howItWorksPage.creatorFeatures.emailUpdates.description'),
            benefits: [
                t('howItWorksPage.creatorFeatures.emailUpdates.benefit1'),
                t('howItWorksPage.creatorFeatures.emailUpdates.benefit2'),
                t('howItWorksPage.creatorFeatures.emailUpdates.benefit3'),
                t('howItWorksPage.creatorFeatures.emailUpdates.benefit4')
            ]
        },
        {
            icon: Users,
            title: t('howItWorksPage.creatorFeatures.audienceManagement.title'),
            description: t('howItWorksPage.creatorFeatures.audienceManagement.description'),
            benefits: [
                t('howItWorksPage.creatorFeatures.audienceManagement.benefit1'),
                t('howItWorksPage.creatorFeatures.audienceManagement.benefit2'),
                t('howItWorksPage.creatorFeatures.audienceManagement.benefit3'),
                t('howItWorksPage.creatorFeatures.audienceManagement.benefit4')
            ]
        },
        {
            icon: Shield,
            title: t('howItWorksPage.creatorFeatures.contentControl.title'),
            description: t('howItWorksPage.creatorFeatures.contentControl.description'),
            benefits: [
                t('howItWorksPage.creatorFeatures.contentControl.benefit1'),
                t('howItWorksPage.creatorFeatures.contentControl.benefit2'),
                t('howItWorksPage.creatorFeatures.contentControl.benefit3'),
                t('howItWorksPage.creatorFeatures.contentControl.benefit4')
            ]
        }
    ];

    const donorFeatures = [
        {
            icon: Heart,
            title: t('howItWorksPage.donorFeatures.flexibleGiving.title'),
            description: t('howItWorksPage.donorFeatures.flexibleGiving.description'),
            benefits: [
                t('howItWorksPage.donorFeatures.flexibleGiving.benefit1'),
                t('howItWorksPage.donorFeatures.flexibleGiving.benefit2'),
                t('howItWorksPage.donorFeatures.flexibleGiving.benefit3'),
                t('howItWorksPage.donorFeatures.flexibleGiving.benefit4')
            ]
        },
        {
            icon: Globe,
            title: t('howItWorksPage.donorFeatures.followMultiple.title'),
            description: t('howItWorksPage.donorFeatures.followMultiple.description'),
            benefits: [
                t('howItWorksPage.donorFeatures.followMultiple.benefit1'),
                t('howItWorksPage.donorFeatures.followMultiple.benefit2'),
                t('howItWorksPage.donorFeatures.followMultiple.benefit3'),
                t('howItWorksPage.donorFeatures.followMultiple.benefit4')
            ]
        },
        {
            icon: MessageCircle,
            title: t('howItWorksPage.donorFeatures.stayConnected.title'),
            description: t('howItWorksPage.donorFeatures.stayConnected.description'),
            benefits: [
                t('howItWorksPage.donorFeatures.stayConnected.benefit1'),
                t('howItWorksPage.donorFeatures.stayConnected.benefit2'),
                t('howItWorksPage.donorFeatures.stayConnected.benefit3'),
                t('howItWorksPage.donorFeatures.stayConnected.benefit4')
            ]
        },
        {
            icon: TrendingUp,
            title: t('howItWorksPage.donorFeatures.trackImpact.title'),
            description: t('howItWorksPage.donorFeatures.trackImpact.description'),
            benefits: [
                t('howItWorksPage.donorFeatures.trackImpact.benefit1'),
                t('howItWorksPage.donorFeatures.trackImpact.benefit2'),
                t('howItWorksPage.donorFeatures.trackImpact.benefit3'),
                t('howItWorksPage.donorFeatures.trackImpact.benefit4')
            ]
        }
    ];

    return (
        <div className="min-h-screen bg-background">
            <HomeHeader />

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 px-6 bg-background">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center max-w-3xl mx-auto mb-12">
                        <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-4">
                            {t('nav.howItWorks')}
                        </span>
                        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6 leading-tight tracking-tight">
                            {t('howItWorksPage.hero.title')}
                        </h1>
                        <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed">
                            {t('howItWorksPage.hero.subtitle')}
                        </p>
                    </div>
                    <div className="aspect-[16/7] w-full overflow-hidden rounded-[2rem] shadow-xl">
                        <img
                            src="/images/serving-meals.jpg"
                            alt="Volunteers serving the community"
                            className="w-full h-full object-cover"
                            loading="eager"
                            {...{ fetchpriority: 'high' }}
                        />
                    </div>
                </div>
            </section>

            {/* Workflow Steps */}
            <section
                ref={workflowRef}
                className={`py-20 px-6 transition-all duration-1000 ${workflowVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('howItWorksPage.workflow.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            {t('howItWorksPage.workflow.subtitle')}
                        </p>
                    </div>

                    <div className="relative">
                        {/* Connecting Line */}
                        <div className="hidden lg:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-primary to-primary/20" style={{ top: '4rem' }}></div>

                        {/* Steps */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {steps.map((step, index) => (
                                <div
                                    key={index}
                                    className={`relative transition-all duration-1000 ${workflowVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                        }`}
                                    style={{ transitionDelay: `${index * 150}ms` }}
                                >
                                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-border hover:shadow-md transition-shadow relative z-10">
                                        {/* Step Number */}
                                        <div className="absolute -top-4 -left-4 w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                                            {step.number}
                                        </div>

                                        {/* Icon */}
                                        <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 mx-auto">
                                            <step.icon className="w-6 h-6 text-primary" />
                                        </div>

                                        {/* Content */}
                                        <h3 className="text-lg font-semibold text-foreground mb-2 text-center">
                                            {step.title}
                                        </h3>
                                        <p className="text-sm text-muted-foreground leading-relaxed text-center">
                                            {step.description}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Creator Features */}
            <section
                ref={creatorRef}
                className={`py-20 px-6 bg-gradient-to-br from-primary/5 to-secondary/10 transition-all duration-1000 ${creatorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('howItWorksPage.creatorFeatures.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {t('howItWorksPage.creatorFeatures.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {creatorFeatures.map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-sm border border-border transition-all duration-1000 ${creatorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                    }`}
                                style={{ transitionDelay: `${index * 200}ms` }}
                            >
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <feature.icon className="w-6 h-6 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-foreground mb-2">
                                            {feature.title}
                                        </h3>
                                        <p className="text-muted-foreground leading-relaxed mb-4">
                                            {feature.description}
                                        </p>
                                    </div>
                                </div>

                                {/* Screenshot */}
                                {feature.title === t('howItWorksPage.creatorFeatures.missionProfile.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/creator-missionprofile.jpg"
                                            alt="Mission Profile Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : feature.title === t('howItWorksPage.creatorFeatures.emailUpdates.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/creator-emails.jpg"
                                            alt="Email Updates Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : feature.title === t('howItWorksPage.creatorFeatures.audienceManagement.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/creator-audience.jpg"
                                            alt="Audience Management Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : feature.title === t('howItWorksPage.creatorFeatures.contentControl.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/creator-contentcontrol.jpg"
                                            alt="Content Control Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl h-48 mb-4 flex items-center justify-center border-2 border-dashed border-secondary/30">
                                        <div className="text-center">
                                            <Video className="w-12 h-12 text-secondary/50 mx-auto mb-2" />
                                            <p className="text-sm text-muted-foreground">Feature Screenshot</p>
                                        </div>
                                    </div>
                                )}

                                {/* Benefits */}
                                <div className="flex flex-wrap gap-2">
                                    {feature.benefits.map((benefit, i) => (
                                        <span
                                            key={i}
                                            className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                                        >
                                            {benefit}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Donor Features */}
            <section
                ref={donorRef}
                className={`py-20 px-6 transition-all duration-1000 ${donorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('howItWorksPage.donorFeatures.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                            {t('howItWorksPage.donorFeatures.subtitle')}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {donorFeatures.map((feature, index) => (
                            <div
                                key={index}
                                className={`bg-white rounded-2xl p-8 shadow-sm border border-border transition-all duration-1000 ${donorVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                    }`}
                                style={{ transitionDelay: `${index * 200}ms` }}
                            >
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="w-12 h-12 bg-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                                        <feature.icon className="w-6 h-6 text-secondary" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-xl font-semibold text-foreground mb-2">
                                            {feature.title}
                                        </h3>
                                        <p className="text-muted-foreground leading-relaxed mb-4">
                                            {feature.description}
                                        </p>
                                    </div>
                                </div>

                                {/* Screenshot */}
                                {feature.title === t('howItWorksPage.donorFeatures.flexibleGiving.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/donor-options.jpg"
                                            alt="Flexible Giving Options Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : feature.title === t('howItWorksPage.donorFeatures.followMultiple.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/donor-multiple.jpg"
                                            alt="Follow Multiple Missions Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : feature.title === t('howItWorksPage.donorFeatures.stayConnected.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/donor-connected.jpg"
                                            alt="Stay Connected Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : feature.title === t('howItWorksPage.donorFeatures.trackImpact.title') ? (
                                    <div className="rounded-xl mb-4 overflow-hidden border border-border shadow-sm h-48 relative">
                                        <img
                                            src="/screenshots/donor-impact.jpg"
                                            alt="Track Your Impact Screenshot"
                                            className="w-full h-auto absolute top-0 left-0"
                                            style={{
                                                animation: 'slideVertical 15s ease-in-out infinite'
                                            }}
                                        />
                                    </div>
                                ) : (
                                    <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl h-48 mb-4 flex items-center justify-center border-2 border-dashed border-primary/30">
                                        <div className="text-center">
                                            <Video className="w-12 h-12 text-primary/50 mx-auto mb-2" />
                                            <p className="text-sm text-muted-foreground">Feature Screenshot</p>
                                        </div>
                                    </div>
                                )}

                                {/* Benefits */}
                                <div className="flex flex-wrap gap-2">
                                    {feature.benefits.map((benefit, i) => (
                                        <span
                                            key={i}
                                            className="px-3 py-1 bg-secondary/20 text-secondary-foreground text-xs font-medium rounded-full"
                                        >
                                            {benefit}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                ref={faqRef}
                className={`py-20 px-6 bg-gradient-to-br from-primary/5 to-secondary/10 transition-all duration-1000 ${faqVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                            {t('howItWorksPage.faq.title')}
                        </h2>
                        <p className="text-lg text-muted-foreground">
                            {t('howItWorksPage.faq.subtitle')}
                        </p>
                    </div>
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div
                                key={index}
                                className="bg-white rounded-2xl p-6 shadow-sm border border-border"
                            >
                                <h3 className="text-lg font-semibold text-foreground mb-2">
                                    {faq.question}
                                </h3>
                                <p className="text-muted-foreground leading-relaxed">
                                    {faq.answer}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section
                ref={ctaRef}
                className={`py-24 bg-primary transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                    }`}
            >
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        {t('howItWorksPage.cta.title')}
                    </h2>
                    <p className="text-xl text-white/90 mb-10 leading-relaxed">
                        {t('howItWorksPage.cta.subtitle')}
                    </p>
                    <div className="flex justify-center">
                        <Button
                            size="lg"
                            className="bg-white hover:bg-white/90 text-primary text-lg px-8 py-6 rounded-full font-semibold group"
                            onClick={() => navigate('/signup-creator')}
                        >
                            {t('howItWorksPage.cta.button1')}
                            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}
